import { AlertTriangle, RefreshCw, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { chamarApi, ErroApi } from '@/nucleo/auth/cliente-api';
import { rotuloDoPapel } from '@/nucleo/auth/papeis';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { Spinner } from '@/ui/Spinner';

interface Estado {
  fase: 'SUCESSO' | 'VAZIO' | 'ERRO';
  identidade: {
    nome: string | null;
    email: string | null;
    papeis: string[];
    mfaVerificada: boolean;
  };
  recorteAtivo: { tipo: string; id: string | null; rotulo: string };
  modulos: Array<{
    id: string;
    titulo: string;
    descricao: string;
    disponivel: boolean;
    caminho: string | null;
  }>;
  indicadores: Array<{ id: string; rotulo: string; valor: number }>;
  alertas: Array<{ tipo: string; mensagem: string }>;
  comandos: Array<{ verbo: string; habilitado: boolean }>;
  erro: { mensagem: string; correlacao: string | null } | null;
}
interface Envelope {
  contract_version: string;
  screen: 'operacao-inicio';
  event: { verb: string; outcome: string; at: string };
  state: Estado;
}

export function OperacaoInicio() {
  const [estado, setEstado] = useState<Estado | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [falha, setFalha] = useState<string | null>(null);
  const carregar = useCallback(async (verbo = 'consultar_estado') => {
    setCarregando(true);
    setFalha(null);
    try {
      const caminho =
        verbo === 'consultar_estado'
          ? '/bff/v1/operacao-inicio/state'
          : `/bff/v1/operacao-inicio/verbs/${verbo}`;
      const envelope = await chamarApi<Envelope>(caminho, {
        metodo: verbo === 'consultar_estado' ? 'GET' : 'POST',
      });
      setEstado(envelope.state);
    } catch (erro) {
      setFalha(
        erro instanceof ErroApi ? erro.aviso : 'Não foi possível carregar a área operacional.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);
  useEffect(() => {
    void carregar();
  }, [carregar]);
  if (carregando && !estado)
    return (
      <div className="flex min-h-48 items-center justify-center" role="status">
        <Spinner size="lg" />
        <span className="sr-only">Carregando área operacional</span>
      </div>
    );
  if (falha && !estado)
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>{falha}</AlertDescription>
        <Button variant="outline" className="mt-3" onClick={() => void carregar()}>
          Tentar novamente
        </Button>
      </Alert>
    );
  if (!estado) return null;
  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Área de operação</h1>
          <p className="text-sm text-muted-foreground">
            Conteúdo limitado ao papel e território desta sessão.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => void carregar('atualizar_estado')}
          disabled={carregando}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Atualizar
        </Button>
      </header>
      {estado.alertas.map((a) => (
        <Alert key={a.mensagem}>
          <AlertDescription>{a.mensagem}</AlertDescription>
        </Alert>
      ))}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Seu acesso</CardTitle>
            <CardDescription>Identidade contextual mínima projetada pelo BFF.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p>
              <strong>Nome:</strong> {estado.identidade.nome ?? 'não informado'}
            </p>
            <p>
              <strong>E-mail:</strong> {estado.identidade.email ?? 'não informado'}
            </p>
            <p>
              <strong>Recorte ativo:</strong> {estado.recorteAtivo.rotulo}
            </p>
            <div className="flex flex-wrap gap-2">
              {estado.identidade.papeis.map((p) => (
                <Badge key={p}>{rotuloDoPapel(p as never)}</Badge>
              ))}
            </div>
            {estado.identidade.mfaVerificada && (
              <p className="flex gap-2 border-t pt-3 text-muted-foreground">
                <ShieldCheck className="h-4 w-4 text-success" />
                Segundo fator confirmado neste acesso.
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Indicadores do recorte</CardTitle>
            <CardDescription>Contagens reais, consultadas sob RLS.</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            {estado.indicadores.map((i) => (
              <div key={i.id} className="rounded-lg border p-3">
                <p className="text-2xl font-semibold">{i.valor}</p>
                <p className="text-xs text-muted-foreground">{i.rotulo}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
      <section>
        <h2 className="mb-3 text-lg font-semibold">Módulos permitidos</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {estado.modulos
            .filter((m) => m.disponivel)
            .map((m) => (
              <Card key={m.id}>
                <CardHeader>
                  <CardTitle className="text-base">{m.titulo}</CardTitle>
                  <CardDescription>{m.descricao}</CardDescription>
                </CardHeader>
              </Card>
            ))}
        </div>
        {!estado.modulos.some((m) => m.disponivel) && (
          <p className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
            Nenhum módulo disponível neste contexto.
          </p>
        )}
      </section>
    </div>
  );
}
export default OperacaoInicio;
